// @ts-strict-ignore
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomValidators } from '@shared/custom-validators';
import { ProjectModel } from '@project/model/project.model';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectVO } from '@project/model/vo/project.vo';
import { IsProjectType } from '@project/model/is-project-type';
import { ProjectComponentPresenter } from '../../../project-component.presenter';
import { FinalizeProjectComponent } from '@project/components/project/finalize-project/finalize-project.component';

@Component({
  standalone: false,
  selector: 'finalize-legacy-project',
  templateUrl: './finalize-legacy-project.component.html',
})
export class FinalizeLegacyProjectComponent implements OnInit, OnDestroy {

  @Input() existingFNumber: string;
  public form: FormGroup;
  @Output() public finish = new EventEmitter<boolean>();

  public readonly fNumberTextInputName: string = 'fNumberTextInput';
  public readonly tcVersionInputName: string = 'tcVersionInput';
  public readonly drawingVersionInputName: string = 'drawingVersionInput';
  public readonly articleNumberLongInputName: string = 'articleNumberLongInput';
  public readonly articleNumberInputName: string = 'articleNumberInput';
  public readonly drawingNumberInputName: string = 'drawingNumberInput';
  public readonly onlyDrawingCheckboxName: string = 'onlyDrawingCheckbox';

  public isRailBasedProject: boolean = true;

  constructor(
    private bsModalRef: BsModalRef,
    private formBuilder: FormBuilder,
    private localeResourceManager: LocaleResourceManager,
    private projectModel: ProjectModel,
    private projectComponentPresenter: ProjectComponentPresenter,
  ) { }

  public ngOnInit(): void {
    this.form = this.createForm();
    this.initFormControls();
    this.validateForm();
  }

  private createForm(): FormGroup<any> {
    const formData: any = {};

    this.isRailBasedProject = this.isMcsOrRailOrFreeMountingProject(this.projectModel.currentProject);
    if (this.isRailBasedProject) {
      formData[this.fNumberTextInputName] = new FormControl(this.existingFNumber,
        Validators.compose([CustomValidators.trimRequired, CustomValidators.maxLength(8), CustomValidators.minLength(8)]));
      formData[this.drawingVersionInputName] = new FormControl(null,  Validators.compose([CustomValidators.trimRequired]));
      formData[this.onlyDrawingCheckboxName] = new FormControl();
    } else {
      formData[this.fNumberTextInputName] = new FormControl(this.existingFNumber,
        Validators.compose([CustomValidators.maxLength(8), CustomValidators.minLength(8)]));
    }

    formData[this.tcVersionInputName] = new FormControl(null, Validators.compose([CustomValidators.trimRequired]));
    formData[this.articleNumberLongInputName] = new FormControl();
    formData[this.articleNumberInputName] = new FormControl();
    formData[this.drawingNumberInputName] = new FormControl();

    return this.formBuilder.group(formData);
  }

  private initFormControls(): void {
    this.form.controls.fNumberTextInput.setValue('');

    this.trySetFormControlStringValue('tcVersionInput', '0');
    this.trySetFormControlStringValue('drawingVersionInput', '0');

    if (this.projectModel && this.projectModel.currentProject) {
      this.trySetFormControlValue('fNumberTextInput', this.projectModel.currentProject.fNumber,
        value => !!value && value.length > 1 ? value.replace(/^F/, "") : undefined);

      if (!this.isMcsOrRailOrFreeMountingProject(this.projectModel.currentProject)) {
        this.trySetFormControlValue('tcVersionInput', this.projectModel.currentProject.documentVersion,
          value => !!value && value > -1 ? value.toString(10) : undefined);
      }

      this.trySetFormControlValue('drawingVersionInput', this.projectModel.currentProject.documentVersion,
        value => !!value && value > -1 ? value.toString(10) : undefined);

      this.trySetFormControlValue('articleNumberLongInput', this.projectModel.currentProject.articleNumberLong,
        value => !!value && !value.isEmpty() ? value.reducedArticleNumberLong : undefined);

      this.trySetFormControlStringValue('articleNumberInput', this.projectModel.currentProject.articleNumber);
      this.trySetFormControlStringValue('drawingNumberInput', this.projectModel.currentProject.drawingNumber);
    }
  }

  private trySetFormControlStringValue(controlName: string,
                                       value: string | undefined) {
    this.trySetFormControlValue(controlName, value, value => value);
  }

  private trySetFormControlValue<T>(controlName: string,
                                    value: T | undefined,
                                    valueFunc: (value: T | undefined) => string | undefined): void {
    if (this.form.controls[controlName]) {
      const calculatedValue = valueFunc(value);
      if (calculatedValue) {
        this.form.controls[controlName].setValue(calculatedValue);
      }
    }
  }

  public validateForm(): boolean {
    if (!this.form.valid) {
      return false;
    }
    if (!this.form.controls.fNumberTextInput.value) {
      return false;
    }
    if (this.form.controls.fNumberTextInput.value.length < 7) {
      return false;
    }
    return true;
  }

  private isMcsOrRailOrFreeMountingProject(project: ProjectVO): boolean {
    if (project && (IsProjectType.mcsGds(project) || IsProjectType.mounting(project))) {
      return true;
    }
    return false;
  }

  public ngOnDestroy(): void {
  }

  public close(closeTypeSuccess: boolean = false): void {
    this.bsModalRef.hide();
    this.finish.emit(closeTypeSuccess);
  }

  public finalizeButtonClickHandler(): void {
    if (!this.validateForm()) {
      return;
    }
    this.close();
    const finalFNumber: string = this.localeResourceManager.getValueByKey('finalizeLegacyProject.label.fNumberPreFix') + this.form.controls.fNumberTextInput.value;
    const documentVersion: number = this.isMcsOrRailOrFreeMountingProject(this.projectModel.currentProject) ? parseInt(this.form.controls.drawingVersionInput.value, 10) + 1 : parseInt(this.form.controls.tcVersionInput.value, 10) + 1;
    let tcVersion: number  = this.isMcsOrRailOrFreeMountingProject(this.projectModel.currentProject) ? parseInt(this.form.controls.tcVersionInput.value, 10) : -1;
    const onlyDrawing: boolean = this.isMcsOrRailOrFreeMountingProject(this.projectModel.currentProject) ? this.form.controls.onlyDrawingCheckbox.value === true : false;
    if (!onlyDrawing) {
      tcVersion++;
    }
    const data: Partial<FinalizeProjectComponent> = {
      mcsGds221LtlExport: IsProjectType.among(this.projectModel.currentProjectType, 'MCS', 'GDS', 'LTL', 'SPLICING_CONNECTOR_221'),
      legacyExport: true,
      articleNumber: this.form.controls.articleNumberInput.value,
      articleNumberLong: this.form.controls.articleNumberLongInput.value,
      drawingNumber: this.form.controls.drawingNumberInput.value,
      documentVersion: documentVersion,
      tcVersion: tcVersion,
      onlyDrawing: onlyDrawing,
      fNumber: finalFNumber,
    };
    this.projectComponentPresenter.showFinalizeProjectPopup(data);
  }
}
